import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { isDefined, isObjectEmpty } from '../utils';
import {
  ApiErrorMessage,
  CONTENT_TYPE_HEADER_KEY,
  MULTIPART_FORM_DATA_CONTENT_TYPE,
} from './constants';
import { ApiFailureResult, ApiRequestConfig, ApiSuccessResult } from './types';

export const SuccessResultSymbol = Symbol('success');
export const FailureResultSymbol = Symbol('failure');

export function createRequestConfig<D>(requestConfig: ApiRequestConfig<D>) {
  const axiosReqConfig: AxiosRequestConfig = {};

  if (requestConfig.query) {
    axiosReqConfig.params = requestConfig.query;
  }

  if (requestConfig.headers) {
    axiosReqConfig.headers = { ...requestConfig.headers };
  }

  axiosReqConfig.withAuth = requestConfig.withAuth ?? true;
  axiosReqConfig.accessToken = requestConfig.accessToken;
  if (requestConfig.responseType) {
    axiosReqConfig.responseType = requestConfig.responseType;
  }

  if (requestConfig.data !== undefined && requestConfig.data !== null) {
    axiosReqConfig.data = requestConfig.data;

    if (
      typeof FormData !== 'undefined' &&
      requestConfig.data instanceof FormData
    ) {
      axiosReqConfig.headers = {
        ...(axiosReqConfig.headers ?? {}),
        [CONTENT_TYPE_HEADER_KEY]: MULTIPART_FORM_DATA_CONTENT_TYPE,
      };
    } else if (isObjectEmpty(requestConfig.data)) {
      delete axiosReqConfig.data;
    }
  }

  return axiosReqConfig;
}

export const createApiSuccessResult = <D>(
  data: D,
  response: AxiosResponse,
  code: number = 200,
): ApiSuccessResult<D> => ({
  success: true,
  failure: false,
  code: code ?? 200,
  value: data,
  cause: null,
  meta: response,
  [SuccessResultSymbol]: true,
});

export const createApiFailureResult = (
  result?: Partial<ApiFailureResult>,
): ApiFailureResult => ({
  success: false,
  failure: true,
  value: null,
  message: result?.message ?? ApiErrorMessage.General,
  code: result?.code ?? -1,
  cause: result?.cause,
  [FailureResultSymbol]: true,
});

export const isApiSuccessResult = <D>(
  value: unknown,
): value is ApiSuccessResult<D> =>
  (value as ApiSuccessResult<D>)?.[SuccessResultSymbol] === true;
export const isApiFailureResult = (value: unknown): value is ApiFailureResult =>
  (value as ApiFailureResult)?.[FailureResultSymbol] === true;

export const createAuthHeaderValue = (token: string) => `Bearer ${token}`;

export const buildQueryParams = (params: Record<string, unknown>) => {
  const query: Record<string, string> = {};

  Object.entries(params).forEach(([key, value]) => {
    if (isDefined(value) === false) {
      return;
    }

    query[key] = String(value);
  });

  return query;
};
